import React, { useEffect } from "react";
import { Alert, Box, Button, FormHelperText, LinearProgress, Paper, Stack, Typography } from "@mui/material";

import { AsyncOperationState } from "fond/async/redux";
import { PolygonState } from "fond/project/polygon/redux";
import { closePolygonSelect, POLYGON_SELECT_UPLOAD_ID, polygonSelectUpload } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

interface ImportDataProgressProps {
  /** Name of the layer being imported. */
  title?: string;
  onSuccess(): void;
  onCancel(): void;
}

/**
 * Progress and retry for importing the data selected by polygon select.
 */
const ImportDataProgress: React.FC<ImportDataProgressProps> = ({ title, onSuccess, onCancel }) => {
  const dispatch = useAppDispatch();
  const { polygonState, convertToDualSided } = useAppSelector((state) => state.project.polygon);
  const uploads = useAppSelector((state) => state.project.projects[state.project.projectId].uploads);

  const upload = uploads[POLYGON_SELECT_UPLOAD_ID];

  useEffect(() => {
    if (polygonState !== PolygonState.upload && upload?.status === AsyncOperationState.success) {
      onSuccess();
    }
  }, [onSuccess, polygonState, upload?.status]);

  const handleRetry = () => {
    dispatch(polygonSelectUpload(convertToDualSided));
  };

  const handleCancel = () => {
    dispatch(closePolygonSelect());
    onCancel();
  };

  if (upload?.status === AsyncOperationState.failure) {
    return (
      <Box data-testid="import-data-panel" px={1}>
        {title && <Typography fontWeight="500">{title}</Typography>}
        <Alert severity="error" sx={{ my: 2 }}>
          There was a problem saving your data. Please try again.
        </Alert>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="text" size="small" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={handleRetry}>
            Retry
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-testid="import-data-panel" px={1}>
      {title && <Typography fontWeight="500">{title}</Typography>}
      <Paper variant="outlined" sx={{ py: 2, px: 2.75, my: 2 }}>
        {upload?.status === AsyncOperationState.executing && (
          <Typography variant="body1" fontWeight={500}>
            In progress
          </Typography>
        )}
        <LinearProgress />
        <FormHelperText color="primary">Uploading data</FormHelperText>
      </Paper>
      <Alert severity="info">FOND is now processing your data, and it will be visible on the map shortly.</Alert>
    </Box>
  );
};

export default ImportDataProgress;
